import { useState } from "react";
import { renderGameToString } from "../../game";
import { Car } from "../../types/CarTypes";
import { Game } from "../../types/GameTypes";
import { Box } from "../Box";
import { BoxRow } from "../BoxRow";
import { StatsVisualiser } from "../StatsVisualizer";
import { Overlay } from "./Overlay";
import "./ResultOverlay.css";

type ResultOverlayProps = {
  game: Game;
  gameNumber: number;
  targetCar: Car;
  onDismissed: () => void;
}

const getWinningText = (game: Game):string => {
  switch(game.round) {
    case 1: return "Formula One!";
    case 2: return "Two Fast, Two Furious";
    case 3: return "Three On The Tree";
    case 4: return "Four On The Floor";
    case 5: 
    default: 
      return "Running On Fumes";
  } 
}

type ClipboardShareStatus = {
  shared: boolean;
  message: string;
  success: boolean;
  rendering?: string;
}

export const ResultOverlay: React.FC<ResultOverlayProps> = ({game, gameNumber, targetCar, onDismissed}: ResultOverlayProps) => {
  const [clipboardShareStatus, setClipboardShareStatus] = useState<ClipboardShareStatus>({shared: false, message: "", success: false});
  if (game.gameResult === "InProgress") return null;
  if (game.gameResult === "NotStarted") return null;
  
  const shareResult = async () => {
    const rendering = renderGameToString(game, gameNumber);
    const { message, success } = await copyToClipboard(rendering);
    setClipboardShareStatus({
      shared: true,
      message,
      success,
      rendering
    });
  }

  const renderShareArea = () => {
    return (
      <>
      {((!clipboardShareStatus.shared) || (clipboardShareStatus.shared && clipboardShareStatus.success)) &&
      <button className="tube" onClick={shareResult}>Share ➽</button>
      }
      {clipboardShareStatus.shared && clipboardShareStatus.success &&
        <p>{clipboardShareStatus.message} ✅</p>
      }
      {clipboardShareStatus.shared && !clipboardShareStatus.success &&
        <>
          <textarea style={{height: "100%"}}>{clipboardShareStatus?.rendering}</textarea>
        </>
      }
    </>);
  }

  if (game.gameResult === "Lost") {
    return <Overlay onDismissed={onDismissed} suffix="Result" dismissButtonText="Close">
      <div className="ResultOverlay-Loss">
        <h2>You lost</h2>
        <h4>
          The car was the&nbsp;<b>{targetCar.manufacturer.name}</b>&nbsp;<i>{targetCar.model}</i>
        </h4>
      
       <BoxRow key="result-box-row">
        <Box
          key={`result-row-manufacturer-${targetCar.manufacturer.name}`}
          mode="Wrong" 
          field="manufacturer"
          mainText={targetCar.manufacturer.name} 
          subText={targetCar.manufacturer.parent?.name} />
        <Box 
          key={`result-row-model-${targetCar.model}`}
          mode="Wrong" 
          field="model"
          mainText={targetCar.model} />
        <Box 
          key={`result-row-bodyStyle-${targetCar.bodyStyle}`}
          mode="Wrong" 
          field="bodyStyle"
          mainText={targetCar.bodyStyle.name} 
          subText={targetCar.bodyStyle.family} />
        <Box 
          key={`result-row-madeIn-${targetCar.manufacturer.country}`} 
          mode="Wrong" 
          field="madeIn"
          mainText={targetCar.manufacturer.country.name}
          subText={targetCar.manufacturer.country.continent} />
        <Box 
          key={`result-row-poweredBy-${targetCar.poweredBy}`}
          mode="Wrong" 
          field="poweredBy"
          mainText={targetCar.poweredBy} />
       </BoxRow>
       <hr />
       <StatsVisualiser mostRecentGameNumber={gameNumber} suffix="L"/>
       {renderShareArea()}
       <br/>
       </div>
    </Overlay> 
    
  } 


  const text = getWinningText(game);
  return (
    <>
    <div className="Overlay-Win Upper">
      {text}
    </div>

    <div className={`Overlay-Win Lower ${(game.gameResult === "Won") && 'shown'}`}>
      <StatsVisualiser mostRecentGameNumber={gameNumber} suffix="W"/>
      {renderShareArea()}
    </div>
    </>
  )
  
}
async function copyToClipboard(s: string): Promise<{message: string, success: boolean}> {
  try {
    // Try Chrome-style first...
    // https://developer.mozilla.org/en-US/docs/Mozilla/Add-ons/WebExtensions/Interact_with_the_clipboard
    const name = "clipboard-write" as PermissionName;
    const permissionResult = await navigator.permissions.query({name})
    if (permissionResult.state === "granted" || permissionResult.state === "prompt") {
      await navigator.clipboard.writeText(s);
    } else {
      return {
        message: "Not allowed to share",
        success: false
      }
    }
  } catch (e) {
    try {
      // Probably Safari ...
      // https://stackoverflow.com/questions/60418518/how-to-copy-to-clipboard-in-safari-using-jquery-javascript
      const el = document.createElement('textarea');
      el.value = s;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);

    } catch (ee) {
      return {
        message: "Unable to share to clipboard",
        success: false
      }
    }
  }

  return {
    message: "Shared to clipboard",
    success: true
  }
  
}

